// The bars chart's template, chart.bars (contract §8.3): BarChart's vertical
// bars (cloud/web/src/components/charts/BarChart.tsx) at the chart's board
// height of 168 px, one slot group per bucket, oldest first, from one
// engine-hours or voyage-distance feed (§4.8). The latest bar with data is in
// the chart line and the rest in ink, as BarChart colours them; a bucket with
// no data is a dash on the baseline, and a partial one is drawn lighter.
//
// A page cannot style an element by what follows it, and a bound element
// holds nothing: so each bucket's bar is drawn twice, once in ink in its
// slot, and once in the chart line over it, those second bars written newest
// first after the slots. The first of them with data shows; every one after
// it, an older bucket, is hidden.
//
// The Marina look (cloud ADR 0396, TOM-678): the latest bar takes the feed's
// kind, engine hours the engine's and voyage distance navigation's.

import { css, domainCss, h, output } from "./kit.js";
import { chartWindow } from "./chart-trend.js";

const SLOTS_MAX = 14;

const CSS = css(`
  .bc { position: relative; display: grid; grid-auto-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr);
    height: 168px; flex: none; padding: 18px 4px 20px; }
  .bc-base { position: absolute; left: 4px; right: 4px; bottom: 20px; border-top: 1px solid var(--grid); }
  .bc-s { position: relative; min-width: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: flex-end; }
  .bc-v { font-size: 11px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums;
    white-space: nowrap; margin-bottom: 4px; }
  .bc-b, .bc-n { display: block; width: 60%; max-width: 24px; min-height: 3px; border-radius: 3px 3px 0 0;
    height: calc(var(--frac,0) * (100% - 18px)); font-size: 0; }
  .bc-b { flex: none; background: var(--text); }
  .bc-n { position: relative; align-self: end; justify-self: center; background: var(--_c); }
  .bc-n:not([data-quality=missing]) ~ .bc-n, .bc-n[data-quality=missing] { display: none; }
  .bc-b[data-quality=partial] { opacity: 0.45; }
  .bc-n[data-quality=partial] { background: color-mix(in srgb, var(--_c) 45%, var(--background)); }
  /* At night the card's colour mixes in near-black under the tint; the bar and the ink under it are one red there. */
  html[data-theme=night] .bc-n[data-quality=partial] { background: var(--_c); opacity: 0.45; }
  .bc-e { position: absolute; left: 50%; top: 40%; transform: translate(-50%, -50%); margin: 0;
    white-space: nowrap; font-size: 13px; color: var(--muted); }
  .bc-n:not([data-quality=missing]) ~ .bc-e { display: none; }
  .bc-b[data-quality=missing] { height: 0; min-height: 0; border-top: 2px dashed var(--muted); border-radius: 0; background: none; }
  .bc-l { position: absolute; bottom: -18px; font-size: 11px; color: var(--muted); white-space: nowrap; }
  .bc-u { display: none; }
`);

/**
 * A card too narrow for every bucket's value and label (TOM-1001): only the
 * latest bucket keeps its value, and every other label shows, counted from
 * the latest, as BarChart thins them. About 34 px a bucket fits both.
 */
const narrowCss = (slots) =>
  css(`
  .bc { container-type: inline-size; }
  @container (max-width: ${slots * 34}px) {
    .bc-s:not(:last-of-type) .bc-v { visibility: hidden; }
    .bc-s:nth-last-of-type(2n) .bc-l { visibility: hidden; }
  }
`);

/** chart.bars: a bars chart's buckets through its feed (§8.3). */
export function chartBars(source, context) {
  const spec = source.spec;
  const metric =
    spec.metric === "voyage_distance" ? "voyage-distance" : "engine-hours";
  const by = spec.by === "week" ? "week" : "day";
  const want = context?.bars?.slots;
  const slots = Number.isInteger(want)
    ? Math.max(1, Math.min(SLOTS_MAX, want))
    : 7;
  const feed = { id: "bars", kind: metric };
  if (metric === "engine-hours")
    feed.engine = typeof spec.engine === "string" ? spec.engine : null;
  feed.by = by;
  feed.slots = slots;
  // A live chart's window rolls (the latest buckets); a fixed one stays.
  if (!(typeof source.rolling === "number" && source.rolling > 0)) {
    const window = chartWindow(spec, null, "");
    if (window.includes("/")) feed.window = window;
  }
  const cells = [];
  for (let i = 0; i < slots; i += 1) {
    const at = { "data-tc-feed": "bars", "data-tc-slot": i };
    cells.push(
      h(
        "div",
        { class: "bc-s", style: `grid-area:1/${i + 1}` },
        h("span", { class: "bc-v", "data-tc-field": "value", ...at }),
        h("i", { class: "bc-b", "data-tc-field": "bar", ...at }),
        h("span", { class: "bc-l", "data-tc-field": "label", ...at }),
      ),
    );
  }
  // The chart line's bars, newest first, each over its slot.
  for (let i = slots - 1; i >= 0; i -= 1)
    cells.push(
      h("i", {
        class: "bc-n",
        style: `grid-area:1/${i + 1}`,
        "data-tc-field": "bar",
        "data-tc-feed": "bars",
        "data-tc-slot": i,
      }),
    );
  // An empty window says so, after the bars that decide whether it shows.
  cells.push(h("p", { class: "bc-e" }, "No data in this window"));
  return output("chart.bars", 1, {
    html: h(
      "div",
      {
        class: "tp",
        "data-domain": metric === "engine-hours" ? "engine" : "navigation",
      },
      h("div", { class: "bc" }, h("span", { class: "bc-base" }), cells),
      h("span", {
        class: "bc-u",
        "data-tc-feed": "bars",
        "data-tc-field": "unit",
      }),
    ),
    css:
      CSS +
      narrowCss(slots) +
      domainCss([metric === "engine-hours" ? "engine" : "navigation"]),
    feeds: [feed],
    card: {
      kind: "chart",
      window: true,
      chip: { feed: "bars", field: "total" },
    },
  });
}
